import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { SimpleAutocomplete } from '~/modules/common/components/SearchAutocomplete';
import { useTaskDropdownHandlers } from './hooks';

const useStyles = makeStyles(theme => ({
  hierarchyOption: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    paddingLeft: 'calc(var(--hierarchy-level, 0) * 24px)'
  },
  parentPlaceholder: {
    opacity: 0.6,
    fontStyle: 'italic'
  },
  closedTask: {
    opacity: 0.6
  }
}));

export const AdvancedSearchableTaskDropdown = ({
  label,
  value,
  onChange,
  disabled = false,
  projectId,
  showClosedTasks: showClosedTasksProp
}) => {
  const intl = useIntl();
  const classes = useStyles();

  const [showClosedTasks, setShowClosedTasks] = useState(
    showClosedTasksProp ?? false
  );
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value?.displayText || '');

  const handleToggleClosedTasks = useCallback(() => {
    setShowClosedTasks(prev => !prev);
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleInputChangeWrapper = useCallback(
    (event, newInputValue, reason) => {
      if (reason === 'reset') {
        setInputValue(value?.displayText || '');
      } else if (reason === 'input') {
        setInputValue(newInputValue);
      }
    },
    [value]
  );

  const handleInputKeyDown = useCallback(
    event => {
      if (event.key === ' ' && !open && !event.target.value) {
        event.preventDefault();
        setOpen(true);
      }
    },
    [open]
  );

  const {
    loading,
    hasMore,
    options,
    handleInputChange,
    handleChange,
    getOptionLabel,
    getOptionSelected,
    getOptionDisabled,
    renderInput: renderInputFromHook,
    renderOption,
    filterOptions,
    showClosedTasksOptionId
  } = useTaskDropdownHandlers({
    value,
    showClosedTasks,
    onToggleClosedTasks: handleToggleClosedTasks,
    onClose: handleClose,
    projectId,
    onChange,
    label,
    classes,
    onInputChange: handleInputChangeWrapper
  });

  const renderInput = useCallback(
    params => {
      const inputProps = {
        ...params.inputProps,
        onKeyDown: event => {
          handleInputKeyDown(event);

          if (params.inputProps.onKeyDown) {
            params.inputProps.onKeyDown(event);
          }
        }
      };

      return renderInputFromHook({ ...params, inputProps });
    },
    [renderInputFromHook, handleInputKeyDown]
  );

  const filteredValue =
    value && value.id === showClosedTasksOptionId ? null : value;

  return (
    <SimpleAutocomplete
      dataQeId="TaskDropdown"
      hasMore={hasMore}
      loading={loading}
      forcePopupIcon
      fullWidth
      value={filteredValue}
      inputValue={inputValue}
      disabled={disabled || !projectId}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      onChange={handleChange}
      onInputChange={handleInputChange}
      getOptionLabel={getOptionLabel}
      options={options}
      renderInput={renderInput}
      renderOption={renderOption}
      getOptionSelected={getOptionSelected}
      getOptionDisabled={getOptionDisabled}
      optionTypeText={intl.formatMessage({ id: 'moreOptions.tasks' })}
      filterOptions={filterOptions}
      disableCloseOnSelect
    />
  );
};

AdvancedSearchableTaskDropdown.propTypes = {
  value: PropTypes.any,
  label: PropTypes.node,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  projectId: PropTypes.string,
  showClosedTasks: PropTypes.bool
};

export default AdvancedSearchableTaskDropdown;
